/**
 * TasksPage.tsx — Task Manager (Evo Brain Phase 1)
 * Leads-style module: a full-width table with row checkboxes, a SelectionActionBar
 * for bulk actions, header controls (New / Run / Pause / mode SegmentedControl),
 * and a route-based detail (/tasks/:id → TaskDetailPage). No autonomy unless the
 * mode is set to 'autonomous' — and even then the daily cap, non-root spawn and
 * allowedTools confinement stay active (autonomous only drops the per-task approve).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Icon } from '../components/Icon.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { Textarea } from '../components/ui/Textarea.js';
import { Select } from '../components/ui/Select.js';
import { Button } from '../components/ui/Button.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { PillGroup, Pill, type PillTone } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { parseServerDate } from '../lib/date.js';
import {
  useTasks, useCreateTask, useRunnerStatus,
  usePauseRunner, useResumeRunner, useSetRunMode,
  useBulkDeleteTasks, useBulkRetryTasks,
  type Task, type TaskStatus, type RunMode,
} from '../hooks/useTasks.js';

const STATUS_TONES: Record<TaskStatus, PillTone> = {
  pending: 'neutral',
  needs_approval: 'amber',
  approved: 'cyan',
  in_progress: 'blue',
  done: 'success',
  failed: 'danger',
  blocked: 'muted',
};
const PRIORITY_TONES: Record<string, PillTone> = { alta: 'danger', media: 'amber', baja: 'muted' };

const GRID = 'calc(var(--evo-list-pad-x) + 16px + var(--evo-list-checkbox-gap)) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 1.6fr)';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const colStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...extra,
});
const headStyle: React.CSSProperties = {
  padding: '8px 12px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.88px',
};

// ── Create modal (unchanged behaviour) ───────────────────────────────────────

function CreateTaskModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('general');
  const [priority, setPriority] = useState('media');
  const [repeatMinutes, setRepeatMinutes] = useState('');
  const create = useCreateTask();

  function submit() {
    if (!title.trim()) return;
    create.mutate({
      title: title.trim(), description: description.trim(), type, priority,
      ...(repeatMinutes.trim() ? { repeatMinutes: Math.max(1, parseInt(repeatMinutes)) } : {}),
    }, { onSuccess: () => onCreated() });
  }

  return (
    <Modal open onClose={onClose} size="md" title={t('tasks.modal.title')} preventClose={create.isPending}
      footer={
        <>
          <Button variant="outline" tone="muted" size="sm" disabled={create.isPending} onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="solid" tone="cyan" size="sm" loading={create.isPending} onClick={submit}>{t('tasks.modal.create')}</Button>
        </>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <Input label={`${t('tasks.fields.title')} *`} value={title} onChange={setTitle} autoFocus fullWidth />
        <Textarea label={t('tasks.fields.description')} value={description} onChange={setDescription} rows={3} fullWidth />
        <Select label={t('tasks.fields.type')} value={type} onChange={v => setType(v ?? 'general')}
          options={[
            { value: 'general', label: t('tasks.type.general') },
            { value: 'research', label: t('tasks.type.research') },
            { value: 'write', label: t('tasks.type.write') },
            { value: 'disk-check', label: t('tasks.type.disk-check') },
          ]} />
        <Select label={t('tasks.fields.priority')} value={priority} onChange={v => setPriority(v ?? 'media')}
          options={[
            { value: 'alta', label: t('tasks.priority.alta') },
            { value: 'media', label: t('tasks.priority.media') },
            { value: 'baja', label: t('tasks.priority.baja') },
          ]} />
        <Input label={t('tasks.fields.repeatMinutes')} type="number" value={repeatMinutes} onChange={setRepeatMinutes} fullWidth />
      </div>
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function TasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  const { data, isLoading } = useTasks(status ? { status } : {});
  const { data: runner } = useRunnerStatus();
  const pause = usePauseRunner();
  const resume = useResumeRunner();
  const setMode = useSetRunMode();
  const bulkDelete = useBulkDeleteTasks();
  const bulkRetry = useBulkRetryTasks();

  const tasks = (data?.tasks ?? []).filter(tk =>
    !search.trim() || tk.title.toLowerCase().includes(search.trim().toLowerCase()));

  function toggleSelect(id: string) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function clearSelection() { setSelected(new Set()); }

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    const paused = runner?.paused ?? false;
    setOverride({
      title: t('tasks.pageTitle'),
      heroIcon: 'bolt-large',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <PillGroup size="md">
          <SegmentedControl<RunMode>
            value={runner?.mode ?? 'approval'}
            onChange={m => setMode.mutate(m)}
            options={[
              { value: 'approval', label: t('tasks.mode.approval') },
              { value: 'autonomous', label: t('tasks.mode.autonomous') },
            ]}
            size="md"
          />
          {paused
            ? <Pill tone="success" onClick={() => resume.mutate()}>{t('tasks.runner.run')}</Pill>
            : <Pill tone="warn" onClick={() => pause.mutate()}>{t('tasks.runner.pause')}</Pill>}
          <Pill tone="cyan" onClick={() => setShowCreate(true)}>{t('tasks.newButton')}</Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t, runner?.mode, runner?.paused, setMode, pause, resume]);

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <FilterRow>
        <FilterRow.Search value={search} onChange={setSearch} onSubmit={() => {}} onClear={() => setSearch('')} placeholder={t('tasks.searchPlaceholder')} />
        <FilterRow.Dropdown<string>
          value={status}
          placeholder={t('tasks.filters.allStatuses')}
          options={[
            { value: '', label: t('tasks.filters.allStatuses') },
            { value: 'needs_approval', label: t('tasks.status.needs_approval') },
            { value: 'approved', label: t('tasks.status.approved') },
            { value: 'pending', label: t('tasks.status.pending') },
            { value: 'in_progress', label: t('tasks.status.in_progress') },
            { value: 'done', label: t('tasks.status.done') },
            { value: 'failed', label: t('tasks.status.failed') },
            { value: 'blocked', label: t('tasks.status.blocked') },
          ]}
          onChange={setStatus}
        />
      </FilterRow>

      {(runner?.paused || runner?.capReached) && (
        <div style={{ padding: 'var(--evo-space-2) var(--evo-space-5)', background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid var(--evo-stroke)', display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Icon name="bolt" size={14} />
          <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
            {runner?.paused ? t('tasks.runner.pausedBanner') : t('tasks.runner.capReached', { runs: runner?.runsToday, cap: runner?.dailyCap })}
          </span>
        </div>
      )}

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('tasks.selected', { count: selected.size })}
        onClearSelection={clearSelection}
      >
        <SelectionActionBar.Action
          label={t('tasks.actions.retry')}
          loading={bulkRetry.isPending}
          confirmRequired
          onClick={() => bulkRetry.mutate(Array.from(selected), {
            onSuccess: r => { clearSelection(); toast.info(t('tasks.bulk.retried', { retried: r.retried, skipped: r.skippedInProgress })); },
          })}
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('tasks.actions.delete')}
          tone="danger"
          loading={bulkDelete.isPending}
          confirmRequired
          onClick={() => bulkDelete.mutate(Array.from(selected), {
            onSuccess: r => { clearSelection(); toast.info(t('tasks.bulk.deleted', { deleted: r.deleted, skipped: r.skippedInProgress })); },
          })}
        />
      </SelectionActionBar>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: GRID, height: 'var(--evo-toolbar-height)', alignItems: 'center', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0 }}>
        <div style={{ padding: '8px var(--evo-list-checkbox-gap) 8px var(--evo-list-pad-x)' }}>
          <Checkbox
            checked={tasks.length > 0 && tasks.every(tk => selected.has(tk.id))}
            indeterminate={selected.size > 0 && !tasks.every(tk => selected.has(tk.id))}
            onChange={() => {
              if (tasks.every(tk => selected.has(tk.id))) tasks.forEach(tk => toggleSelect(tk.id));
              else tasks.filter(tk => !selected.has(tk.id)).forEach(tk => toggleSelect(tk.id));
            }}
          />
        </div>
        <div style={headStyle}>{t('tasks.columns.title')}</div>
        <div style={headStyle}>{t('tasks.columns.type')}</div>
        <div style={headStyle}>{t('tasks.columns.status')}</div>
        <div style={headStyle}>{t('tasks.columns.priority')}</div>
        <div style={headStyle}>{t('tasks.columns.nextRun')}</div>
        <div style={headStyle}>{t('tasks.columns.attempts')}</div>
        <div style={headStyle}>{t('tasks.columns.lastResult')}</div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && tasks.length === 0 && <EmptyState size="md" reserveSpace title={t('tasks.empty')} />}
        {tasks.length > 0 && (
          <TimeBandedList<Task, string>
            items={tasks}
            getId={tk => tk.id}
            getTimestamp={tk => tk.createdAt}
            checkedIds={selected}
            onBandCheck={(ids, allChecked) => ids.forEach(id => {
              const isSel = selected.has(id);
              if (allChecked && isSel) toggleSelect(id);
              else if (!allChecked && !isSel) toggleSelect(id);
            })}
            collapsedBands={collapsedBands}
            onToggleBand={band => setCollapsedBands(prev => { const next = new Set(prev); next.has(band) ? next.delete(band) : next.add(band); return next; })}
            renderItem={tk => (
              <div key={tk.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  data-list-item-id={tk.id}
                  onClick={() => navigate(`/tasks/${tk.id}`)}
                  className="cursor-pointer transition-colors"
                  style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'flex-start', minHeight: 'var(--evo-list-row-min-height)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,250,200,0.04)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                >
                  <div style={{ padding: '14px var(--evo-list-checkbox-gap) 14px var(--evo-list-pad-x)' }} onClick={e => { e.stopPropagation(); toggleSelect(tk.id); }}>
                    <div style={{ pointerEvents: 'none' }}><Checkbox checked={selected.has(tk.id)} onChange={() => {}} /></div>
                  </div>
                  <div style={{ padding: '14px 12px 14px 0', overflow: 'hidden' }}>
                    <span className="truncate" style={{ display: 'block', color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)' }}>{tk.plainSummary ?? tk.title}</span>
                  </div>
                  <div style={colStyle()}>{t(`tasks.type.${tk.type}`, { defaultValue: tk.type })}</div>
                  <div style={{ padding: '14px 12px', display: 'flex' }}>
                    <Pill tone={STATUS_TONES[tk.status] ?? 'neutral'} size="xs">{t(`tasks.status.${tk.status}`)}</Pill>
                  </div>
                  <div style={{ padding: '14px 12px', display: 'flex' }}>
                    <Pill tone={PRIORITY_TONES[tk.priority] ?? 'muted'} size="xs">{t(`tasks.priority.${tk.priority}`, { defaultValue: tk.priority })}</Pill>
                  </div>
                  <div style={colStyle()}>{fmt(tk.nextRunAt)}</div>
                  <div style={colStyle()}>{tk.attempts}</div>
                  <div style={colStyle()}>{tk.result ? tk.result.replace(/\s+/g, ' ').slice(0, 80) : (tk.lastError ? `⚠ ${tk.lastError.slice(0, 70)}` : '—')}</div>
                </div>
                <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)' }} />
              </div>
            )}
          />
        )}
      </div>

      {showCreate && <CreateTaskModal onClose={() => setShowCreate(false)} onCreated={() => setShowCreate(false)} />}
    </div>
  );
}
